import {
  Check,
  Edit01,
  Microphone01,
  PauseSquare,
  Trash01,
  X,
} from '@insight-health-ai/roseate/icons';
import {
  BarsLoader,
  Button,
  toast,
} from '@insight-health-ai/roseate/components';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { doStream, endStream } from 'src/modules/ambient_solo/utils/listen';
import {
  useUpdateEncounterDetails,
  useStartEncounter,
  useLegacyEncounterStore,
} from 'src/hooks/encounter';
import { Encounter, EncounterPhase, TranscriptionMessage } from 'src/models';
import moment from 'moment';
import { useGetTranscriptionList } from 'src/hooks/transcription';
import { Input } from 'antd';
import { ScreenLoader } from 'src/components';
import { useUpdateTranscription } from 'src/hooks/transcription';
import { analytics } from 'src/services';
import { sortTranscripts } from 'src/utils';
import { useAuth } from '@frontegg/react';
import type { EncounterPreChartProps } from './encounterPreChart';
import { useMicrophoneBadge } from 'src/hooks/vim/hooks/useMicrophoneBadge';

export type RecordTranscriptionProps = Pick<EncounterPreChartProps, 'mode'> & {
  status: Encounter['pre_chart_status'];
  encounterId: string;
  encounterPhase: EncounterPhase;
  selectedMicrophone: string;
  onRecordingChange?: (recording: {
    active: boolean;
    since: number | null;
  }) => void;
  onChange?: (transcription: TranscriptionMessage[]) => void;
  isExpanded?: boolean;
  legacySearchFilters: unknown;
  onTranscriptionChange?: () => void;
};

export const RecordTranscription = forwardRef<
  {
    stopRecording: () => Promise<void>;
  },
  RecordTranscriptionProps
>(
  (
    {
      mode,
      status,
      encounterId,
      encounterPhase,
      onRecordingChange,
      selectedMicrophone,
      isExpanded = true,
      onChange,
      onTranscriptionChange,
      /**
       * @todo: Harshit | remove this post migration
       */
      legacySearchFilters,
    },
    ref
  ) => {
    const { user } = useAuth();
    const [, setTimer] = useState(0);
    const [recording, setRecording] = useState<{
      active: boolean;
      since: number | null;
    }>({
      active: false,
      since: null,
    });

    useMicrophoneBadge(recording.active);

    const [livePartialTranscript, setLivePartialTranscript] = useState('');
    const [liveTranscript, setLiveTranscript] = useState<any[]>([]);
    const [fullTranscript, setFullTranscript] = useState<
      TranscriptionMessage[]
    >([]);

    const [editingTranscriptIndex, setEditingTranscriptIndex] = useState<
      number | null
    >(null);
    const editingTranscriptTextRef = useRef<string | undefined>(undefined);

    const {
      data: transcriptionList,
      isLoading: isLoadingTranscriptionList,
      refetch: refetchTranscriptionList,
    } = useGetTranscriptionList(
      {
        encounterId,
        encounterPhase,
        type: 'live',
      },
      {
        refetchOnWindowFocus: recording.active,
      }
    );

    const {
      mutateAsync: updateTranscription,
      isPending: isUpdatingTranscription,
    } = useUpdateTranscription();

    const transcription = transcriptionList?.transcriptions?.[0];

    const toggleRecording = (value: boolean) => {
      const newValue = { active: value, since: value ? Date.now() : null };
      setRecording(newValue);
      onRecordingChange?.(newValue);
    };

    const { mutateAsync: updateEncounterDetails } = useUpdateEncounterDetails();
    const { mutateAsync: startEncounter } = useStartEncounter();
    const { updateEncounterList } = useLegacyEncounterStore();

    const handleStartRecording = async () => {
      try {
        if (status === 'new') {
          analytics.track(
            `Clicked Start Pre-charting In Ambient From ${mode === 'vca' ? 'VCA' : 'Aura'}`,
            {
              email: user?.email,
            }
          );
          await startEncounter({
            encounterId,
            encounterPhase,
            payload: {},
          });
        } else {
          analytics.track(
            `Clicked Resume Pre-charting In Ambient From ${mode === 'vca' ? 'VCA' : 'Aura'}`,
            {
              email: user?.email,
            }
          );
          await updateEncounterDetails({
            encounterId,
            encounterPhase,
            payload: {},
          });
        }

        /**
         * @todo: Harshit | remove this post migration
         */
        try {
          updateEncounterList(legacySearchFilters, {
            currentEncounterId: encounterId,
          });
        } catch (error) {
          // do nothing
        }

        await new Promise((resolve, reject) => {
          (doStream as any)({
            encounterId,
            encounterPhase,
            setMicLevel: () => {},
            setLiveTranscript,
            setLivePartialTranscript,
            selectedMicrophoneId: selectedMicrophone,
            successCallback: () => {
              resolve(true);
            },
            failureCallback: (error: any) => {
              reject(error);
            },
            setDictation: () => {},
            isDictate: false,
            transcriptTimeoutCallback: (error: any) => {
              // @todo: | Harshit handle error
            },
          });
        });

        toggleRecording(true);
      } catch (error) {
        toast.error('Something went wrong. Please try again.');
      }
    };

    const handleStopRecording = async () => {
      endStream();
      toggleRecording(false);
      setLivePartialTranscript('');
      setLiveTranscript([]);
      setTimeout(() => {
        refetchTranscriptionList();
      }, 1000);
    };

    useImperativeHandle(ref, () => ({
      stopRecording: handleStopRecording,
    }));

    useEffect(() => {
      if (transcription?.transcription_json) {
        setFullTranscript(sortTranscripts(transcription.transcription_json));
      }
    }, [transcription]);

    useEffect(() => {
      let triggerTranscriptionChange = false;
      setFullTranscript((currentFullTranscript) => {
        let combinedTranscripts;

        if (recording.active) {
          // When actively transcribing, filter out potential duplicates
          const newLiveTranscripts = liveTranscript.filter((liveItem: any) => {
            const timestampMinute = moment(liveItem.timestamp).format(
              'YYYY-MM-DDTHH:mm:ss'
            );

            // Check if this item already exists in fullTranscript
            return !currentFullTranscript.some(
              (fullItem) =>
                moment(fullItem.timestamp).format('YYYY-MM-DDTHH:mm:ss') ===
                  timestampMinute &&
                fullItem.speaker === liveItem.speaker &&
                fullItem.text?.includes(liveItem.text)
            );
          });

          // Only proceed if there are new unique items
          if (newLiveTranscripts.length === 0) return currentFullTranscript;

          combinedTranscripts = [
            ...currentFullTranscript,
            ...newLiveTranscripts,
          ];
        } else {
          // When not transcribing (processing final results), use original approach
          if (!liveTranscript.length) return currentFullTranscript;
          combinedTranscripts = [...currentFullTranscript, ...liveTranscript];
        }

        const groupedTranscripts = combinedTranscripts.reduce(
          (acc, current) => {
            // Format timestamp to the minute in browser timezone
            const timestampMinute = moment(current.timestamp).format(
              'YYYY-MM-DDTHH:mm:ss'
            );

            // Find if there's already an entry for this timestamp and speaker
            const existingEntryIndex = acc.findIndex(
              (entry: any) =>
                entry.timestamp === timestampMinute &&
                entry.speaker === current.speaker
            );

            if (existingEntryIndex !== -1) {
              // Only append if the text isn't already included when transcribing
              // Otherwise just append as in the original code
              if (
                recording.active &&
                acc[existingEntryIndex].text.includes(current.text)
              ) {
                // Skip if already included during active transcription
              } else {
                acc[existingEntryIndex].text += ` ${current.text}`;
              }
            } else {
              // If it's a new minute or a new speaker, push a new entry
              acc.push({
                timestamp: timestampMinute,
                text: current.text,
                speaker: current.speaker,
              });
            }
            return acc;
          },
          []
        );

        // Update the state to hold the grouped and sorted transcripts
        triggerTranscriptionChange = true;
        return sortTranscripts(groupedTranscripts);
      });
      if (triggerTranscriptionChange) {
        onTranscriptionChange?.();
      }
    }, [liveTranscript, livePartialTranscript, recording.active]);

    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
      if (recording.active) {
        intervalRef.current = setInterval(() => {
          setTimer((prev) => prev + 1);
        }, 1000);
      }
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }, [recording.active]);

    useEffect(() => {
      onChange?.(fullTranscript);
    }, [fullTranscript]);

    const renderTranscription = () => {
      if (isLoadingTranscriptionList) {
        return (
          <div className="flex items-center justify-center p-4 min-h-[240px]">
            <ScreenLoader />
          </div>
        );
      }

      if (fullTranscript.length > 0) {
        return (
          <div className="flex flex-col gap-4 p-4 max-h-[min(50vh,320px)] overflow-y-auto">
            {fullTranscript.map((item, index) => (
              <div key={index} className="flex flex-col gap-1">
                <div className="flex gap-2 items-center justify-between">
                  {item.timestamp && (
                    <div className="font-medium">
                      {moment(item.timestamp).isSame(moment(), 'day')
                        ? moment(item.timestamp).format('h:mm a')
                        : moment(item.timestamp).format('MMM DD, YYYY h:mm a')}
                    </div>
                  )}
                  {!recording.active &&
                    typeof editingTranscriptIndex !== 'number' && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          appearance="secondary"
                          variant="text"
                          icon={<Edit01 />}
                          disabled={isUpdatingTranscription}
                          onClick={() => {
                            setEditingTranscriptIndex(index);
                            editingTranscriptTextRef.current = item.text;
                          }}
                        />
                        <Button
                          size="sm"
                          appearance="secondary"
                          variant="text"
                          icon={<Trash01 />}
                          disabled={isUpdatingTranscription}
                          onClick={() => {
                            const newFullTranscript = fullTranscript.filter(
                              (_, i) => i !== index
                            );
                            updateTranscription({
                              transcriptionId: transcription?.transcription_id!,
                              payload: {
                                transcription_json: newFullTranscript,
                              },
                            }).catch(() => {
                              toast.error('Failed to update transcription');
                            });
                            onTranscriptionChange?.();
                          }}
                        />
                      </div>
                    )}
                  {editingTranscriptIndex === index && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        appearance="danger"
                        variant="text"
                        icon={<X />}
                        disabled={isUpdatingTranscription}
                        onClick={() => {
                          setEditingTranscriptIndex(null);
                          editingTranscriptTextRef.current = undefined;
                        }}
                      />
                      <Button
                        size="sm"
                        appearance="primary"
                        variant="text"
                        icon={<Check />}
                        disabled={isUpdatingTranscription}
                        onClick={() => {
                          if (editingTranscriptTextRef.current !== item.text) {
                            const newFullTranscript = fullTranscript.map(
                              (t, i) =>
                                i === index
                                  ? {
                                      ...t,
                                      text: editingTranscriptTextRef.current,
                                    }
                                  : t
                            );
                            updateTranscription({
                              transcriptionId: transcription?.transcription_id!,
                              payload: {
                                transcription_json: newFullTranscript,
                              },
                            }).catch(() => {
                              toast.error('Failed to update transcription');
                            });
                            onTranscriptionChange?.();
                          }
                          setEditingTranscriptIndex(null);
                          editingTranscriptTextRef.current = undefined;
                        }}
                      />
                    </div>
                  )}
                </div>
                {editingTranscriptIndex === index ? (
                  <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    defaultValue={item.text}
                    onChange={(e) => {
                      editingTranscriptTextRef.current = e.target.value;
                    }}
                  />
                ) : (
                  <div className="text-sm text-gray-500">{item.text}</div>
                )}
              </div>
            ))}
          </div>
        );
      }

      return (
        <div className="flex flex-col gap-2 items-center justify-center p-4 min-h-[240px]">
          {recording.active ? (
            <>
              <div className="relative flex items-center justify-center h-12 w-12 bg-danger/10 rounded-full">
                <div className="absolute inset-0 rounded-full animate-ping bg-danger/30"></div>
                <Microphone01 className="text-danger text-xl" />
              </div>
              <div className="text-sm text-gray-500">
                Speak to start transcribing
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center h-12 w-12 bg-primary/10 rounded-full">
                <Microphone01 className="text-primary text-xl" />
              </div>
              <div className="text-sm text-gray-500">
                Click below to start recording
              </div>
              <Button
                appearance="primary"
                icon={<Microphone01 />}
                onClick={handleStartRecording}
              >
                {fullTranscript.length ? 'Resume recording' : 'Start recording'}
              </Button>
            </>
          )}
        </div>
      );
    };

    const renderStickyActions = () => {
      if (recording.active) {
        return (
          <div className="flex gap-2 items-center">
            <div className="text-lg font-medium">
              {new Date(Date.now() - recording.since!)
                .toISOString()
                .slice(14, 19)}
            </div>
            <BarsLoader />
            <div className="flex align-center w-full">
              {livePartialTranscript}
            </div>
            <div className="right-0 ml-auto flex-shrink-0">
              <Button
                appearance="danger"
                variant="text"
                icon={<PauseSquare />}
                onClick={() => {
                  handleStopRecording();
                  analytics.track(
                    `Clicked Stop Pre-charting In Ambient From ${mode === 'vca' ? 'VCA' : 'Aura'}`,
                    {
                      email: user?.email,
                    }
                  );
                }}
              >
                Stop Recording
              </Button>
            </div>
          </div>
        );
      }

      if (isExpanded && fullTranscript.length > 0) {
        return (
          <div className="flex gap-2 items-center justify-center">
            <Button
              appearance="primary"
              variant="text"
              icon={<Microphone01 />}
              onClick={handleStartRecording}
            >
              Resume recording
            </Button>
          </div>
        );
      }

      return null;
    };

    return (
      <div>
        {isExpanded && renderTranscription()}
        <div className="sticky bottom-0 items-center w-full border-t border-gray-200 p-2 empty:hidden">
          {renderStickyActions()}
        </div>
      </div>
    );
  }
);
